import { ContentService } from '@blk/cwp-ng-framework';
import { DZ_COLOR_BLK_BLACK, DZ_COLOR_BLK_WHITE, DZ_COLOR_NEUTRAL_200 } from '@blk/daisy/core/styles';
import { AbstractRenderer, Alignment, BoxModelConstraint, BreakableRenderer, BusinessComponent, BusinessComponentOptions, CellPanel, ColumnComposition, ColumnPanel, DotRenderer, ImageRenderer, LayoutStrategy, RowPanel, SquareRenderer, StaticParagraphComponent } from '@blk/simba';
import { InjectConfig, IqConfigKey } from '@ddt-web/config-layer';
import { ComponentData, TabUtil } from '@ddt-web/data-service';
import { OutputFlag } from '@ddt-web/domain';
import { FundAnalysisDataTransformerUtil, FundData, StatusChange, StatusColour } from '@ddt-web/fund-stats';
import { IconRenderer } from '@ddt-web/pdf-utils';
import { ColDef, ColGroupDef } from 'ag-grid-community';
import * as flatten from 'lodash/flatten';
import { Observable } from 'rxjs';
import { first } from 'rxjs/operators';

import { SvgImage } from '../helpers/static-pages/static-section/svg-image';


export class FundHealthStatusAnalysis extends BusinessComponent {
	@InjectConfig(IqConfigKey.ENABLE_STATUS_CHANGES) private readonly isCompareEnabled = false;
	@InjectConfig(IqConfigKey.ENABLE_FUND_STATUS_INTERACTIONS) public readonly enableStatusInteractions = false;

	private taxQualifiedAnalysisType: string;
	private emptyCell = new StaticParagraphComponent(null, '.', {}, { useFullHeight: true }, ['text-white']);

	private bottomBorder = { borderBottom: 0.5, borderBottomColor: DZ_COLOR_NEUTRAL_200 };
	private rightBorder = { borderRight: 0.5, borderRightColor: DZ_COLOR_NEUTRAL_200 };
	private leftBorder = { borderLeft: 0.5, borderLeftColor: DZ_COLOR_NEUTRAL_200 };
	private topBorder = { borderTop: 0.5, borderTopColor: DZ_COLOR_NEUTRAL_200 };

	constructor(
		name: string,
		public dataComponentId: string,
		public colDefs: Array<ColGroupDef | ColDef>,
		private outputFlags: OutputFlag[],
		private taxQualifiedAnalysisType$: Observable<string>,
		public visible: boolean = true
	) {
		super(name, 'BusinessComponent', BusinessComponentOptions.merge({ visible: visible, componentId: dataComponentId }));
		this.taxQualifiedAnalysisType$.pipe(first()).subscribe((taxQualifiedAnalysisType: string) => {
			this.taxQualifiedAnalysisType = taxQualifiedAnalysisType;
		});
	}

	public buildComponent(componentData: ComponentData, contentService: ContentService): AbstractRenderer<SVGGElement>[] {
		const panels = [];

		if (componentData && componentData.success) {
			const hardcodedHeadingMap = flatten(
				this.colDefs.map(col => col?.['children'] ? col?.['children']?.map(aof => aof) : col)
			);
			const columnProportions = this.getColProportions();

			const rowData: FundData[][] = FundAnalysisDataTransformerUtil.transformComponentData(
				componentData,
				null,
				null,
				componentData,
				this.getSelectedStatuses(),
				null,
				null,
				this.taxQualifiedAnalysisType,
				null
			);

			const rows = [];
			// for each morningstar category
			rowData.forEach(morningstarFundGroup => {
				morningstarFundGroup.forEach(row => {
					if (!!row?.isMorningstarCategory?.rawValue) {
						rows.push(
							new RowPanel('Morningstar Category', [
								new CellPanel(
									[new StaticParagraphComponent(null, row?.category?.formattedValue, {}, { paddingLeft: 5, paddingTop: 3, paddingBottom: 2 })],
									{
										borderBottom: 0.5,
										borderBottomColor: DZ_COLOR_BLK_BLACK, ...this.leftBorder, ...this.rightBorder,
										proportion: 100
									},
									Alignment.Horizontal.Left
								)
							]));
					} else {
						rows.push(this.generateRowCellsFromRowData(row, hardcodedHeadingMap, columnProportions.statusLowerHeading));
					}
				});
			});

			if (rows.length) {
				panels.push(new StaticParagraphComponent('IQ_PDF_INVESTMENT_DETAILS_TITLE', 'Fund Health Status Analysis', {}, {}, ['text-14', 'text-extra-bold']));
				if (TabUtil.isDataAvailable(componentData, 'as.of.date')) {
					const aodTableData = componentData.getTabById('as.of.date')?.tableData?.[0];
					const aodCells = aodTableData?.cells?.[0];
					const asOfDate = aodCells[0].formattedValue;

					panels.push(new StaticParagraphComponent('IQ_AS_OF_DATE' + asOfDate, 'As of ' + asOfDate, {}, { paddingTop: 6 }));
				}
				panels.push(new StaticParagraphComponent(
					this.generateDescription(),
					'Analysis for funds that need immediate attention, need review and are on track.', {}, { paddingBottom: 10 }
				));

				const heading: ColumnPanel = new ColumnPanel('heading', [
					this.getLegend(),
					this.createHeading(hardcodedHeadingMap, columnProportions, contentService)
				]);

				panels.push(new BreakableRenderer([heading, ...rows], {}, heading, 2));
			}
		}
		return panels;
	}

	private getSelectedStatuses(): string[] {
		const statuses = this.outputFlags.filter(flag => flag.getValue()).map(flag => flag.getName());
		return statuses.length === 3 ? null : statuses;
	}

	private generateRowCellsFromRowData(row: FundData, headingMap: any[], columnProportions: number[]): RowPanel {
		const rowConstraints = null;

		const cells = headingMap.map((col, i, arr) => {
			let cellConstraints = { proportion: columnProportions[i], useFullHeight: true, ...this.leftBorder, ...this.bottomBorder };
			if (i === arr.length - 1) {
				cellConstraints = { ...cellConstraints, ...this.rightBorder };
			}
			let cell = new CellPanel([this.emptyCell], cellConstraints);

			if (!!row[col?.field]?.rawValue) {
				if (col?.cellRenderer === 'statusCellComponent') {
					cell = this.createStatusCell(row, col, cellConstraints);
				} else {
					if (col?.field === 'description' && !!row?.status?.attribute?.fundIdea?.isFundIdea) {
						cell = this.createFundIdeaNameCell(row, col, cellConstraints);
					} else {
						cell = new CellPanel(
							[new StaticParagraphComponent(null, row[col.field].formattedValue, {}, { paddingLeft: 3, paddingTop: 2.5, useFullHeight: true })],
							cellConstraints, Alignment.Horizontal.Left, null, Alignment.Vertical.Middle
						);
					}
				}
			}
			return cell;
		});
		return new RowPanel('Fund', cells, rowConstraints);
	}

	private createFundIdeaNameCell(row: FundData, col, cellConstraints: Partial<BoxModelConstraint>): CellPanel {
		return new CellPanel(
			[new StaticParagraphComponent(null, row[col.field].formattedValue, {}, { paddingLeft: 3, paddingTop: 2.5, useFullHeight: true })],
			!!row?.status?.attribute?.fundIdea?.isSuggestedAddition ? { ...cellConstraints, backgroundColor: '#EFE5FA' } : cellConstraints,
			Alignment.Horizontal.Left,
			LayoutStrategy.COLUMN,
			Alignment.Vertical.Middle
		);
	}

	private createStatusCell(row: FundData, col, cellConstraints): CellPanel {
		if (row?.status?.attribute?.fundIdea?.isSuggestedAddition && col?.field === 'status') {
			return new CellPanel(
				[
					new ImageRenderer(
						new SvgImage(`FUND_IDEA_${row[col.field]?.formattedValue?.toUpperCase()}`).getStaticContent(),
						{ paddingLeft: 14, paddingRight: 11, paddingTop: 2.5, useFullWidth: true, useFullHeight: true }
					)
				],
				{ ...cellConstraints },
				Alignment.Horizontal.Center, LayoutStrategy.COLUMN, Alignment.Vertical.Middle
			);
		} else {
			return this.createSimpleStatusCell(row, col, cellConstraints);
		}
	}

	/**
	 * Adds a plain status cell. Add arrow if there is a status change
	 */
	private createSimpleStatusCell(row: FundData, col, cellConstraints) {
		let renderers: AbstractRenderer<SVGElement>[] = [this.emptyCell];
		const statusChange: StatusChange = col?.field === 'status' ? row?.status?.attribute?.statusChange : null; // don't show if aof cell

		const isColumnTaxOffender = col?.field === 'highTaxOffenders' || col?.field === 'worstTaxOffenders';
		const taxOffenderRow = row?.highTaxOffenders || row?.worstTaxOffenders;
		const showGrayStatus: boolean = isColumnTaxOffender ? taxOffenderRow?.attribute?.isTaxQualifiedFund : false;

		const isAccepted = this.enableStatusInteractions && col?.field === 'status' && row?.statusInteraction?.formattedValue === 'accept';
		const isDismissed = this.enableStatusInteractions && col?.field === 'status' && row?.statusInteraction?.formattedValue === 'dismiss';

		if (this.isCompareEnabled) {
			switch (statusChange) {
				case StatusChange.DOWNGRADE:
					renderers = [
						new IconRenderer('arrow-down', 6, 6, DZ_COLOR_BLK_BLACK, { paddingLeft: 2, paddingRight: 19, paddingTop: 2.5, useFullWidth: true, useFullHeight: true })
					];
					break;
				case StatusChange.UPGRADE:
					renderers = [
						new IconRenderer('arrow-up', 6, 6, DZ_COLOR_BLK_BLACK, { paddingLeft: 2, paddingRight: 19, paddingTop: 2.5, useFullWidth: true, useFullHeight: true })
					];
					break;
			}
		}

		if (isAccepted) {
			renderers = [
				...renderers,
				new IconRenderer(
					'check', 6, 6,
					StatusColour.get[row[col.field].formattedValue] === StatusColour.YELLOW ? DZ_COLOR_BLK_BLACK : DZ_COLOR_BLK_WHITE,
					{ paddingLeft: 2.75, paddingTop: 3.5, useFullWidth: true, useFullHeight: true }
				)
			];
		}

		if (isDismissed) {
			const customRender = row?.status?.attribute?.fundIdea?.isFundIdea ?
				new ImageRenderer(
					new SvgImage(`FUND_IDEA_${row[col.field]?.formattedValue?.toUpperCase()}_DISMISS`).getStaticContent(),
					{ paddingLeft: 14, paddingRight: 11, paddingTop: 2.5, useFullWidth: true, useFullHeight: true }
				) :
				new DotRenderer(
					DZ_COLOR_BLK_WHITE,
					{ paddingLeft: 14, paddingTop: 2.5, useFullWidth: true, useFullHeight: true },
					{ size: 8, stroke: StatusColour.get[row[col.field].formattedValue]?.hex, strokeWidth: 1 }
				);
			return new CellPanel(
				[
					...renderers,
					customRender
				],
				{
					...cellConstraints,
					marginTop: 0,
					marginLeft: 0,
					paddingTop: 0,
					paddingLeft: 0
				},
				Alignment.Horizontal.Left,
				LayoutStrategy.COLUMN,
				Alignment.Vertical.Middle
			);
		} else {
			return new CellPanel(
				renderers,
				{
					...cellConstraints,
					backgroundShape: 'circle',
					borderRadius: 4,
					backgroundColor: showGrayStatus ? StatusColour.get['gray']?.hex : StatusColour.get[row[col.field].formattedValue]?.hex,
					marginTop: 0,
					marginLeft: 0,
					paddingTop: 0,
					paddingLeft: 0
				},
				Alignment.Horizontal.Left,
				LayoutStrategy.COLUMN,
				Alignment.Vertical.Middle
			);
		}
	}

	private createHeading(headingsMap, proportions: { [heading: string]: number[] }, contentService: ContentService): ColumnPanel {
		const upperRowComposition = this.colDefs.map((col, i, arr) => {
			let cellConstraints = { ...this.leftBorder, ...this.topBorder, useFullHeight: true };
			if (col['children']) {
				cellConstraints = { ...cellConstraints, ...this.bottomBorder };
			}
			if (i === arr.length - 1) {
				cellConstraints = { ...cellConstraints, ...this.rightBorder };
			}
			return new ColumnComposition({
				cells: col['children'] ? col.headerName : '.',
				widthProportion: proportions.dimensionsUpperHeading[i],
				horizontalAlignment: Alignment.Horizontal.Left,
				verticalAlignment: Alignment.Vertical.Bottom,
				boxModelConstraint: { useFullHeight: true, paddingLeft: 3, paddingTop: 2 },
				textClasses: col['children'] ? ['text-condensed-light'] : ['text-white'],
				cellBoxModelConstraint: cellConstraints
			});
		});

		const lowerRowComposition = headingsMap.map((col, i, arr) => {
			const isAofCol = !col.headerName;
			return new ColumnComposition({
				cells: isAofCol ? contentService.get(col?.headerComponentParams?.headerText?.key, col?.headerComponentParams?.headerText?.defaultText) : col.headerName,
				widthProportion: proportions.statusLowerHeading[i],
				horizontalAlignment: Alignment.Horizontal.Left,
				verticalAlignment: Alignment.Vertical.Bottom,
				boxModelConstraint: { useFullHeight: true, paddingLeft: 3, paddingTop: 2 },
				textClasses: isAofCol ? ['text-condensed-bold'] : ['text-bold'],
				cellBoxModelConstraint:
					i !== arr.length - 1
						? { ...this.leftBorder, ...this.bottomBorder, useFullHeight: true }
						: { ...this.leftBorder, ...this.bottomBorder, ...this.rightBorder, useFullHeight: true }
			});
		});
		const upperRow = new RowPanel('headerUpperRow', upperRowComposition.map(config => config.renderer.render()));
		const lowerRow = new RowPanel('headerLowerRow', lowerRowComposition.map(config => config.renderer.render()));
		return new ColumnPanel('heading', [upperRow, lowerRow]);
	}

	/**
	 * Dynamically generate table proportions of areas of focus from remaining space after having accounted for space of other columns.
	 */
	private getColProportions(): { [heading: string]: number[] } {
		const leftColsProportions = [5, 6, 24];
		const remainingDimensionsProportions = 100 - leftColsProportions.reduce((acc, curr) => acc + curr, 0);

		const numberOfStatuses = this.colDefs.reduce((acc, curr) => acc + (curr?.['children']?.length ?? 0), 0);
		const proportionPerStatus = remainingDimensionsProportions / numberOfStatuses;

		const dimensionsProportions = this.colDefs
			.map(col => col['children'] ? proportionPerStatus * col['children'].length : null)
			.filter(num => !!num);
		const statusProportions = [...Array(numberOfStatuses)].map(i => proportionPerStatus);

		return {
			dimensionsUpperHeading: [...leftColsProportions, ...dimensionsProportions],
			statusLowerHeading: [...leftColsProportions, ...statusProportions]
		};
	}

	private getLegend(): AbstractRenderer<SVGElement> {
		return new ColumnPanel('legend column', [
			this.getStatusLegendRow(),
			this.getMiscLegendRow()
		]);
	}

	private getStatusLegendRow(): RowPanel {
		return new RowPanel('status legend', [
			new CellPanel([], { proportion: 38 }),
			new CellPanel([
				new StaticParagraphComponent('IQ_PDF_INVESTMENT_DETAILS_LEGEND_STATUS_LABEL', 'Status', {}, {}, ['text-bold'])
			], { proportion: 6 }, null, LayoutStrategy.ROW),
			new CellPanel([
				new DotRenderer(StatusColour.RED.hex, { paddingTop: 1.2, paddingRight: 3 }, { size: 8 }),
				new StaticParagraphComponent('FH_CHART_RED', 'FH_CHART_RED')
			], { proportion: 18 }, null, LayoutStrategy.ROW),
			new CellPanel([
				new DotRenderer(StatusColour.YELLOW.hex, { paddingTop: 1.2, paddingRight: 3 }, { size: 8 }),
				new StaticParagraphComponent('FH_CHART_YELLOW', 'FH_CHART_YELLOW')
			], { proportion: 12 }, null, LayoutStrategy.ROW),
			new CellPanel([
				new DotRenderer(StatusColour.GREEN.hex, { paddingTop: 1.2, paddingRight: 3 }, { size: 8 }),
				new StaticParagraphComponent('FH_CHART_GREEN', 'FH_CHART_GREEN')
			], { proportion: 10 }, null, LayoutStrategy.ROW),
			new CellPanel([
				new DotRenderer(StatusColour.GREY.hex, { paddingTop: 1.2, paddingRight: 3 }, { size: 8 }),
				new StaticParagraphComponent('FH_CHART_GRAY', 'FH_CHART_GRAY')
			], { proportion: 16 }, null, LayoutStrategy.ROW)
		], { paddingTop: 5, paddingBottom: 3 });
	}

	private getMiscLegendRow(): RowPanel {

		let spaceProportion = 20;
		if (!this.isCompareEnabled) {
			spaceProportion += 20;
		}
		if (!this.enableStatusInteractions) {
			spaceProportion += 18;
		}

		return new RowPanel('other legend', [
			new CellPanel([], { proportion: spaceProportion }),
			this.enableStatusInteractions ? new CellPanel([
				new IconRenderer('check', 7, 7, DZ_COLOR_BLK_WHITE, { paddingTop: 2, paddingLeft: 1.5 })
			], { proportion: 1.75, backgroundShape: 'circle', backgroundColor: DZ_COLOR_BLK_BLACK, borderRadius: 4 }, null, LayoutStrategy.ROW)
				: null,
			this.enableStatusInteractions ? new CellPanel([
				new StaticParagraphComponent('IQ_ACCEPTED_FUND_LABEL', 'IQ_ACCEPTED_FUND_LABEL')
			], { proportion: 7.25 }, null, LayoutStrategy.ROW)
				: null,
			this.enableStatusInteractions ? new CellPanel([
				new DotRenderer(DZ_COLOR_BLK_WHITE, { paddingRight: 3, paddingTop: 1 }, { size: 8, stroke: DZ_COLOR_BLK_BLACK, strokeWidth: 1 }),
				new StaticParagraphComponent('IQ_DISMISSED_FUND_LABEL', 'IQ_DISMISSED_FUND_LABEL')
			], { proportion: 9 }, null, LayoutStrategy.ROW)
				: null,
			new CellPanel([
				new IconRenderer('lightbulb', 7, 7, DZ_COLOR_BLK_BLACK, { paddingRight: 3, paddingTop: 1 }),
				new StaticParagraphComponent('IQ_BLK_FUND_IDEA_LABEL', 'IQ_BLK_FUND_IDEA_LABEL')
			], { proportion: 12 }, null, LayoutStrategy.ROW),
			this.isCompareEnabled ? new CellPanel([
				new IconRenderer('arrow-up', 7, 7, DZ_COLOR_BLK_BLACK, { paddingRight: 3, paddingTop: 1 }),
				new StaticParagraphComponent('IQ_DOWNGRADED_LEGEND', 'Downgraded')
			], { proportion: 10 }, null, LayoutStrategy.ROW)
				: null,
			this.isCompareEnabled ? new CellPanel([
				new IconRenderer('arrow-down', 7, 7, DZ_COLOR_BLK_BLACK, { paddingRight: 3, paddingTop: 1 }),
				new StaticParagraphComponent('IQ_UPGRADED_LEGEND', 'Upgraded')
			], { proportion: 10 }, null, LayoutStrategy.ROW)
				: null,
			new CellPanel([new SquareRenderer('#EFE5FA', { marginTop: 1, marginRight: 2 }, 8, 8)], { proportion: 2 }),
			new CellPanel([
				new StaticParagraphComponent('IQ_PURPLE_DASH_LEGEND', 'Purple background indicates a suggested addition to your portfolio', {}, {}, ['text-condensed-light'])
			], { proportion: 28 }, Alignment.Horizontal.Left)
		].filter(cell => !!cell), { paddingBottom: 2 });
	}

	private generateDescription(): string {
		let description = 'IQ_PDF_INVESTMENT_DETAILS_DESCRIPTION';
		const selectedStatuses = this.getSelectedStatuses();

		if (!selectedStatuses) {
			return description + '_ALL';
		} else {
			selectedStatuses.forEach(status => description = description + '_' + status.toUpperCase());
			return description;
		}
	}

}
